import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './NoticeBanner.scss';

/**
 * NoticeBanner (always-on, simplified)
 * - Dil dosyasından th(key) ile metin çeker.
 * - items: [{ key: 'noticeBanner.DBMSG94', type?: 'info'|'warning'|'success'|'error', isActive?: boolean }]
 * - items verilmezse noticeBanner.DBMSG94 & DBMSG93 kullanılır.
 * - Oklar, progress bar ve chip/rozet yok.
 */
const NoticeBanner = ({ items, sticky = true, autoScroll = true, scrollInterval = 3000, showDots = true }) => {
    const { th } = useLanguage();

    const defaults = useMemo(
        () => [
            { key: 'noticeBanner.DBMSG95', type: 'error', isActive: true },
            { key: 'noticeBanner.DBMSG94', type: 'warning', isActive: true },
            { key: 'noticeBanner.DBMSG93', type: 'info', isActive: true }
        ],
        []
    );

    const DEFAULT_LINK_MAP = {
        PADA5: 'https://pada5.com/account/login?ReturnUrl=%2Fhome'
    };

    const messages = useMemo(() => {
        const src = Array.isArray(items) && items.length ? items : defaults;
        return src
            .filter((m) => m.isActive !== false)
            .map((m) => ({
                ...m,
                text: th(m.key, { fallback: '' }),
                type: m.type || 'info'
            }))
            .filter((m) => m.text && m.text.trim().length > 0);
    }, [items, defaults, th]);

    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (!autoScroll || paused || messages.length <= 1) return;
        const id = setInterval(() => setIndex((i) => (i + 1) % messages.length), scrollInterval);
        return () => clearInterval(id);
    }, [autoScroll, paused, messages.length, scrollInterval]);

    if (!messages.length) return null;

    const current = messages[index];
    const tone = getTone(current.type);

    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const renderWithLinks = (text) => {
        if (!text) return null;
        const keys = Object.keys(DEFAULT_LINK_MAP);
        if (keys.length === 0) return text;

        const pattern = new RegExp(`\\b(${keys.map((k) => escapeRegExp(k)).join('|')})\\b`, 'g');
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = pattern.exec(text)) !== null) {
            const [found] = match;
            const start = match.index;
            const end = start + found.length;

            if (start > lastIndex) parts.push(text.slice(lastIndex, start));

            parts.push(
                <a key={`${found}-${start}`} href={DEFAULT_LINK_MAP[found]} className="attn-banner__link" target="_blank" rel="noopener noreferrer" title={found}>
                    {found}
                </a>
            );

            lastIndex = end;
        }

        if (lastIndex < text.length) parts.push(text.slice(lastIndex));
        return parts;
    };

    return (
        <section className={`attn-banner ${sticky ? 'attn-banner--sticky' : ''} ${tone.className}`} role="region" aria-label="System notices" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <div className="attn-banner__accent" aria-hidden="true" />

            <div className="attn-banner__content">
                <div className="attn-banner__icon">
                    <i className={`pi ${tone.icon}`} />
                </div>

                <div className="attn-banner__text" role="status" aria-live={current.type === 'error' || current.type === 'warning' ? 'assertive' : 'polite'}>
                    {renderWithLinks(current.text)}
                </div>
            </div>

            {showDots && messages.length > 1 && (
                <div className="attn-banner__dots attn-banner__dots--center" role="tablist">
                    {messages.map((_, idx) => (
                        <button key={idx} className={`dot ${idx === index ? 'is-active' : ''}`} onClick={() => setIndex(idx)} role="tab" aria-selected={idx === index} aria-label={`Mesaj ${idx + 1}`} />
                    ))}
                </div>
            )}
        </section>
    );
};

function getTone(type) {
    switch (type) {
        case 'error':
            return {
                className: 'tone--error',
                icon: 'pi-times-circle',
                badge: (th) => th('noticeBanner.badgeError', { fallback: 'Hata' })
            };
        case 'warning':
            return {
                className: 'tone--warning',
                icon: 'pi-exclamation-triangle',
                badge: (th) => th('noticeBanner.badgeWarning', { fallback: 'Uyarı' })
            };
        case 'success':
            return {
                className: 'tone--success',
                icon: 'pi-check-circle',
                badge: (th) => th('noticeBanner.badgeSuccess', { fallback: 'Başarılı' })
            };
        default:
            return {
                className: 'tone--info',
                icon: 'pi-info-circle',
                badge: (th) => th('noticeBanner.badgeInfo', { fallback: 'Bilgi' })
            };
    }
}

export default NoticeBanner;
